// Any descriptor's edit form, rendered from the field declarations the server
// sent. Reuses `renderInput` from the ticket create screen rather than growing a
// second generic field renderer that would drift from it; the two vocabularies
// meet in `toRenderField` (../../pages/admin/descriptor-form.js).

import { useEffect, useState } from 'react';

import { renderInput } from '../create/CustomFields.jsx';
import ActionBuilder from './ActionBuilder.jsx';
import ConditionBuilder from './ConditionBuilder.jsx';
import KeywordPanel from './KeywordPanel.jsx';
import ColumnsField from './ColumnsField.jsx';
import {
  formFields, toRenderField, parseActionsDraft, showsKeywordPanel,
} from '../../pages/admin/descriptor-form.js';
import {
  actionVocabulary, builderAvailable, actionsToRows, rowProblems,
} from '../../pages/admin/macro-actions.js';
import {
  conditionVocabulary, conditionsToDraft, conditionProblems, parseConditionsDraft,
  builderAvailable as conditionBuilderAvailable,
} from '../../pages/admin/view-conditions.js';

/**
 * The `control: 'actions'` field — now a row builder IN FRONT OF the JSON editor,
 * with the editor still reachable and still the floor.
 *
 * WHY THERE IS A BUILDER AT ALL NOW. Task 11 refused to write one, and it was right:
 * the four verbs and their value vocabularies lived only in
 * src/services/admin/macros.js, so a builder "would have to hardcode a second copy
 * on the client, and the first time the two copies disagreed the builder would
 * quietly rewrite any action it did not recognise into one it did". The answer is
 * not to disprove that; it is to SERVE the vocabulary. The macros descriptor now
 * ships `fields.actions.vocabulary` straight from the validator's own arrays, so the
 * dropdown offers the same objects the gate tests against and drift is
 * unrepresentable inside one process.
 *
 * WHY THAT IS SAFE RATHER THAN MERELY TIDY. There is NO client literal to fall back
 * on. If `vocabulary` is absent — an older API behind a separately deployed
 * `web/dist` — `builderAvailable` is false and the screen is the JSON textarea with
 * a line saying so. A stale client can never render a wrong dropdown, because it has
 * nothing to render one from.
 *
 * WHY THE JSON VIEW IS PERMANENT. A stored array can hold things the builder cannot
 * express — `actions_json`'s only CHECK is one-argument `ISJSON`, and rows written by
 * migration or seed were never validated at all. If the builder replaced the textarea
 * outright, such a macro would become both uneditable AND unviewable.
 *
 * WHY THE DRAFT IS LOCAL STATE. A controlled textarea whose `value` is
 * `JSON.stringify(parsedValue)` cannot be typed into: the first keystroke that makes
 * the text incomplete fails to parse, the parsed value does not change, and React
 * re-renders the old text over what was just typed.
 *
 * WHAT IT REFUSES TO DO. Unparseable text does not become `[]`, and a builder row
 * with no value does not post blank. Both report upward, the Save button goes away,
 * and the last good value is left untouched.
 */
function ActionsField({ id, field, value, fieldKey, setValue, onDraftError, owner }) {
  const vocabulary = actionVocabulary(field);
  const [mode, setMode] = useState(() => (builderAvailable(value, vocabulary) ? 'builder' : 'json'));
  const [draft, setDraft] = useState(() => JSON.stringify(value ?? [], null, 2));
  const [parseError, setParseError] = useState(null);

  // Row problems block Save through the same channel unreadable JSON uses. Derived
  // from the value rather than reported out of the builder: a blank row round-trips
  // to `{ type: …, value: "" }`, so the array alone is enough to see it, and one
  // channel means the two editors cannot disagree about whether Save is safe.
  const rowErrors = mode === 'builder' && vocabulary
    ? rowProblems(actionsToRows(value, vocabulary), vocabulary)
    : [];
  const blocking = mode === 'builder'
    ? (rowErrors.length > 0 ? rowErrors.map((p) => p.message).join(' ') : null)
    : parseError;

  useEffect(() => {
    onDraftError(fieldKey, blocking);
    // Leaving a blocked field behind must not leave the Save button disabled forever.
    return () => onDraftError(fieldKey, null);
  }, [blocking, fieldKey]);

  function edit(text) {
    setDraft(text);
    const result = parseActionsDraft(text);
    setParseError(result.ok ? null : result.error);
    if (result.ok) setValue(fieldKey, result.value);
  }

  if (mode === 'builder') {
    return (
      <ActionBuilder
        id={id}
        value={value}
        vocabulary={vocabulary}
        owner={owner}
        onChange={(next) => {
          setValue(fieldKey, next);
          // Keep the textarea honest, so toggling to it shows what the rows built
          // rather than what the field held when the screen opened.
          setDraft(JSON.stringify(next, null, 2));
        }}
        onShowJson={() => setMode('json')}
      />
    );
  }

  return (
    <>
      <textarea
        id={id}
        rows={6}
        spellCheck={false}
        className="field-input font-mono text-xs"
        value={draft}
        onChange={(event) => edit(event.target.value)}
      />
      <div className="mt-1 flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-xs text-ink-muted">
          {vocabulary
            ? 'A JSON array. Each entry is { "type": …, "value": … }.'
            : 'A JSON array. The server did not describe the action vocabulary, so the row builder is unavailable on this screen.'}
        </p>
        {builderAvailable(value, vocabulary) && (
          <button
            type="button"
            className="btn-ghost shrink-0 px-2 py-0.5 text-xs"
            onClick={() => setMode('builder')}
          >
            Use the row builder
          </button>
        )}
      </div>
      {parseError && (
        <p className="mt-1 text-xs text-red-700 dark:text-red-300">{parseError}</p>
      )}
    </>
  );
}

/**
 * The `control: 'conditions'` field — a row builder in front of the JSON editor,
 * on exactly the terms ActionsField established, and for the same reasons.
 *
 * WHAT IS DIFFERENT, AND WHY IT MATTERS MORE HERE. A macro's actions are ADVICE:
 * an action nobody understands is skipped and the macro still fills in the body.
 * A view's conditions are a WHERE clause, and the two ways they can be wrong are
 * not symmetric. `{"all":[]}` is the column DEFAULT and compiles to `(1=1)`, so a
 * view with nothing in it is a named queue showing the entire desk — which is why
 * `conditionProblems` reports an EMPTY builder as a problem that blocks Save,
 * where an empty ActionBuilder is a perfectly good macro.
 *
 * The JSON view stays permanently reachable and is still the floor: the column's
 * CHECK is `ISJSON(col, OBJECT)`, so a stored object the builder cannot express is
 * physically possible, and such a view must not become both uneditable AND
 * unviewable. Unlike the macro editor, blank text here is NOT a valid value —
 * there is no empty conditions object the compiler will read — so
 * `parseConditionsDraft` refuses it rather than resolving to `{}`.
 */
function ConditionsField({ id, field, value, fieldKey, setValue, onDraftError }) {
  const vocabulary = conditionVocabulary(field);
  const [mode, setMode] = useState(
    () => (conditionBuilderAvailable(value, vocabulary) ? 'builder' : 'json'),
  );
  const [draft, setDraft] = useState(() => JSON.stringify(value ?? { all: [] }, null, 2));
  const [parseError, setParseError] = useState(null);

  // Derived from the VALUE, never reported out of the builder, so the two editors
  // cannot disagree about whether Save is safe — the same single channel
  // ActionsField uses.
  const rowErrors = mode === 'builder' && vocabulary
    ? conditionProblems(conditionsToDraft(value, vocabulary), vocabulary)
    : [];
  const blocking = mode === 'builder'
    ? (rowErrors.length > 0 ? rowErrors.map((p) => p.message).join(' ') : null)
    : parseError;

  useEffect(() => {
    onDraftError(fieldKey, blocking);
    return () => onDraftError(fieldKey, null);
  }, [blocking, fieldKey]);

  function edit(text) {
    setDraft(text);
    const result = parseConditionsDraft(text);
    setParseError(result.ok ? null : result.error);
    if (result.ok) setValue(fieldKey, result.value);
  }

  if (mode === 'builder') {
    return (
      <ConditionBuilder
        id={id}
        value={value}
        vocabulary={vocabulary}
        onChange={(next) => {
          setValue(fieldKey, next);
          // Keep the textarea honest, so toggling to it shows what the rows built
          // rather than what the field held when the screen opened.
          setDraft(JSON.stringify(next, null, 2));
        }}
        onShowJson={() => setMode('json')}
      />
    );
  }

  return (
    <>
      <textarea
        id={id}
        rows={10}
        spellCheck={false}
        className="field-input font-mono text-xs"
        value={draft}
        onChange={(event) => edit(event.target.value)}
      />
      <div className="mt-1 flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-xs text-ink-muted">
          {vocabulary
            ? 'A JSON object with an "all" and/or "any" list of '
              + '{ "field": …, "operator": …, "value": … }.'
            : 'A JSON object. The server did not describe the filter vocabulary, so the row '
              + 'builder is unavailable on this screen.'}
        </p>
        {conditionBuilderAvailable(value, vocabulary) && (
          <button
            type="button"
            className="btn-ghost shrink-0 px-2 py-0.5 text-xs"
            onClick={() => setMode('builder')}
          >
            Use the row builder
          </button>
        )}
      </div>
      {parseError && (
        <p className="mt-1 text-xs text-red-700 dark:text-red-300">{parseError}</p>
      )}
    </>
  );
}

export default function DescriptorForm({
  descriptor, values, setValue, fieldErrors, onDraftError, disabled,
}) {
  const fields = (
    // NOTE the `content-start`: inside the two-column grid below, a stretched
    // fieldset would centre its rows against a nine-row panel.
    <fieldset className="grid content-start gap-4" disabled={disabled}>
      {formFields(descriptor).map(([key, field]) => {
        const rendered = toRenderField(key, field);
        const id = `admin-${descriptor.name}-${key}`;
        const error = fieldErrors?.[key];

        return (
          <div key={key}>
            {rendered.data_type === 'boolean' ? (
              // The label goes BESIDE the box, not above it: a lone checkbox under
              // a heading reads as a control with nothing to do with the words
              // above it. Same treatment CustomFields gives its own `checkbox`.
              <label className="flex items-center gap-2 text-sm text-ink" htmlFor={id}>
                {renderInput(rendered, id, values[key], setValue)}
                {rendered.label}
              </label>
            ) : (
              <>
                <label className="field-label" htmlFor={id}>
                  {rendered.label}
                  {rendered.is_required && (
                    <span className="text-red-600 dark:text-red-400"> *</span>
                  )}
                </label>
                {rendered.data_type === 'actions' ? (
                  <ActionsField
                    id={id}
                    field={field}
                    fieldKey={key}
                    value={values[key]}
                    setValue={setValue}
                    onDraftError={onDraftError}
                    owner={descriptor.name}
                  />
                ) : rendered.data_type === 'columns' ? (
                  <ColumnsField
                    id={id}
                    field={field}
                    fieldKey={key}
                    value={values[key]}
                    setValue={setValue}
                  />
                ) : rendered.data_type === 'conditions' ? (
                  <ConditionsField
                    id={id}
                    field={field}
                    fieldKey={key}
                    value={values[key]}
                    setValue={setValue}
                    onDraftError={onDraftError}
                  />
                ) : renderInput(rendered, id, values[key], setValue)}
              </>
            )}
            {error && (
              <p className="mt-1 text-xs text-red-700 dark:text-red-300">{error}</p>
            )}
          </div>
        );
      })}
    </fieldset>
  );

  if (!showsKeywordPanel(descriptor)) return <div className="mt-4">{fields}</div>;

  // The panel is a SIBLING of the fieldset, never a child: a disabled fieldset
  // disables every descendant form control, `<button>` included, so a reader below
  // lead would get Copy buttons that silently do nothing. Below `xl` it stacks
  // under the form and the Body returns to full width.
  return (
    <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1fr)_18rem]">
      {fields}
      <KeywordPanel />
    </div>
  );
}
